/**
 * Hand-rolled i18n, carried over from the foodtruck app.
 *
 * Three languages and a few hundred strings do not justify an i18n library.
 * A typed dictionary means the compiler checks every key exists in every
 * language, which is the only thing a library would add.
 */

export const LANGUAGES = ["de", "en", "fr", "es"] as const;
export type Language = (typeof LANGUAGES)[number];

export const LANGUAGE_LABELS: Record<Language, string> = {
  de: "Deutsch",
  en: "English",
  fr: "Français",
  es: "Español",
};

export const LOCALES: Record<Language, string> = {
  de: "de-DE",
  en: "en-GB",
  fr: "fr-FR",
  es: "es-ES",
};

const de = {
  // shell
  brand: "dähääm",
  brandSub: "Kurpfälzer Wohnzimmer",
  brandLine: "Café — Bar",
  navMenu: "Karte",
  navReserve: "Reservieren",
  navStory: "Das Haus",
  navHours: "Öffnungszeiten",
  skipToContent: "Zum Inhalt springen",
  language: "Sprache",
  back: "Zurück",
  close: "Schließen",
  loading: "Lädt …",
  retry: "Noch einmal",
  somethingWrong: "Da ist etwas schiefgegangen.",

  // opening state
  openNow: "Jetzt geöffnet",
  closedNow: "Gerade geschlossen",
  closesAt: "bis {time}",
  opensAt: "öffnet {day} um {time}",
  today: "heute",
  tomorrow: "morgen",
  closedLabel: "Ruhetag",

  // hero
  heroEyebrow: "Kurpfälzer Wohnzimmer · Café — Bar",
  heroTitle1: "Ankommen.",
  heroTitle2: "Bleiben.",
  heroTitle3: "Wohlfühlen.",
  heroLead:
    "Ein Wohnzimmer in der Altstadt: Brunch am Morgen, Kaffee und Kuchen am Nachmittag, gute Drinks am Abend. Ohne Steifheit.",
  heroCtaReserve: "Tisch reservieren",
  heroCtaMenu: "Zur Karte",
  heroScroll: "Reinkommen",

  // marquee
  marquee: "Brunch · Café · Bar · Kurpfälzer Wohnzimmer · Mitten in der Altstadt · Zeit zum Bleiben",

  // a day at dähääm
  dayEyebrow: "Ein Tag im dähääm",
  dayTitle: "Von der ersten Tasse bis zum letzten Glas.",
  morningTitle: "Morgens",
  morningTime: "09 – 14 Uhr",
  morningBody: "Der Tag beginnt wie in Mailand: Tramezzini, ein Croissant mit Butter und Honig, ein Cappuccino. Mit dem Laptop am Fenster oder der Zeitung auf der Bank.",
  afternoonTitle: "Nachmittags",
  afternoonTime: "14 – 18 Uhr",
  afternoonBody: "Kuchen vom Konditor, eine Bowl oder die Tagessuppe, ein Kaffee dazu. Ein Ort zum Lernen und Entspannen – wie das Wohnzimmer bei der Oma.",
  eveningTitle: "Abends",
  eveningTime: "18 – 23 Uhr",
  eveningBody: "Cocktails, Wein aus der Pfalz, Dähäämuletta und Kleines aus dem Ofen zum Teilen. Die Lichter gehen runter, die Bar geht auf.",

  // the room
  roomEyebrow: "Unsere Geschichte",
  roomTitle: "Ein Wohnzimmer, kein Restaurant.",
  roomBody1: "Dähääm bedeutet auf Kurpfälzisch „daheim“ – und genau so soll es sich bei uns anfühlen: wie ein gemütliches Wohnzimmer mitten in der Heidelberger Altstadt, in dem man sich morgens, mittags oder abends gerne trifft.",
  roomBody2: "Die Idee zu unseren Tramezzini brachte Alex von einer Reise nach Mailand mit: Dort entdeckte er die typisch italienischen Sandwiches beim Frühstück und wollte dieses Stück italienischer Alltagskultur nach Heidelberg bringen. Heute verbindet Dähääm diese Inspiration mit Tramezzini, Bowls, Suppen, Kaffee und ausgewählten Cocktails – unkompliziert, persönlich und für jede Tageszeit.",
  roomPoint1: "Wohnzimmer statt Restaurant",
  roomPoint2: "Tramezzini wie in Mailand",
  roomPoint3: "Bowls, Suppen, Kaffee & Cocktails",
  roomPoint4: "Unkompliziert, persönlich, zu jeder Tageszeit",

  // signature
  signatureEyebrow: "Aus der Karte",
  signatureTitle: "Was man bei uns probieren sollte.",
  viewFullMenu: "Ganze Karte ansehen",

  // manifesto
  manifestoQuote: "Unser Anspruch ist, dass ihr euch bei uns ganz wie Zuhause fühlt!",
  manifestoBody: "Runterkommen, genießen, feiern in entspannter, heimeliger Atmosphäre – denn dähääm … is immer noch am schönsten!",

  // find us
  findEyebrow: "Öffnungszeiten & Anfahrt",
  findTitle: "Mitten in der Altstadt.",
  findLead: "Zwei Minuten vom Marktplatz, zwischen Buchladen und Weinhandlung. Man findet uns am Licht im Fenster.",
  addressLabel: "Adresse",
  phoneLabel: "Telefon",
  emailLabel: "E-Mail",
  hoursLabel: "Öffnungszeiten",
  openMaps: "Route planen",
  mon: "Montag",
  tue: "Dienstag",
  wed: "Mittwoch",
  thu: "Donnerstag",
  fri: "Freitag",
  sat: "Samstag",
  sun: "Sonntag",

  // reservation
  reserveEyebrow: "Reservierung",
  reserveTitle: "Einen Platz freihalten.",
  reserveLead: "Für bis zu 25 Personen direkt online. Größere Runden, Feiern und das ganze Wohnzimmer: ruft uns kurz an.",
  date: "Datum",
  time: "Uhrzeit",
  guests: "Personen",
  name: "Name",
  phone: "Telefon",
  email: "E-Mail",
  occasion: "Anlass",
  occasionNone: "Einfach so",
  occasionBirthday: "Geburtstag",
  occasionDate: "Date",
  occasionBusiness: "Geschäftlich",
  occasionBrunch: "Brunch mit Freunden",
  notes: "Wünsche",
  notesPlaceholder: "Fensterplatz, Kinderstuhl, Allergien …",
  submitReservation: "Reservierung anfragen",
  sending: "Wird gesendet …",
  reserveSuccessTitle: "Der Tisch ist für euch freigehalten.",
  reserveSuccessBody: "Bestätigungscode {code}. Wir melden uns nur, falls etwas nicht passt — sonst: bis {when}.",
  reserveAnother: "Weitere Reservierung",
  requiredField: "Bitte ausfüllen",
  invalidPhone: "Bitte eine gültige Telefonnummer eingeben",
  invalidEmail: "Bitte eine gültige E-Mail eingeben",
  largeGroupHint: "Ab 26 Personen bitte telefonisch — wir stellen dann gern zusammen.",
  noSlots: "An diesem Tag haben wir Ruhetag.",
  pastSlot: "Dieser Zeitpunkt liegt in der Vergangenheit.",
  reserveSummary: "{guests} Personen · {when}",
  callUs: "Anrufen",
  reservePrivacy: "Mit dem Absenden stimmst du zu, dass wir deine Angaben zur Bearbeitung der Reservierung verwenden. Details:",
  seating: "Sitzplatz",
  seatingInside: "Drinnen",
  seatingOutside: "Draußen",
  seatingAny: "Egal",
  openHint: "Geöffnet {open} – {close} Uhr",
  timeClosed: "Um diese Zeit haben wir geschlossen.",
  closedDayTitle: "An diesem Tag haben wir Ruhetag.",
  closedDayBody: "Bitte wähle einen anderen Tag.",
  openDaysLabel: "Geöffnet: {days}",

  // menu
  menuEyebrow: "Die Karte",
  menuTitle: "Morgens, mittags, abends.",
  menuLead: "Alles hausgemacht, viel aus der Region — und alles zum Hierbleiben gedacht.",
  allergens: "Allergene",
  vegan: "vegan",
  vegetarian: "vegetarisch",
  seasonal: "saisonal",
  houseFavourite: "Hausliebling",
  soldOut: "Ausverkauft",
  from: "ab",
  menuNote: "Zum Hierbleiben. Karte und Preise wechseln mit der Saison.",
  choiceOf: "Wahlweise",


  // footer
  footerTagline: "Modernes Kurpfälzer Wohnzimmer.",
  footerRights: "Alle Rechte vorbehalten",
  imprint: "Impressum",
  privacy: "Datenschutz",
  followUs: "Folgen",
  newsletterTitle: "Neuigkeiten aus dem Wohnzimmer",
  newsletterBody: "Saisonkarte, Weinabende, Sonntagsbrunch — einmal im Monat, nie öfter.",
  newsletterPlaceholder: "deine@mail.de",
  newsletterCta: "Eintragen",
  newsletterDone: "Danke! Wir melden uns.",

  // dashboard (staff)
  dashTitle: "Reservierungsbuch",
  dashSub: "Website · WhatsApp · Telefon",
  dashLoginTitle: "Nur fürs Team",
  dashUsername: "Benutzername",
  dashPassword: "Passwort",
  dashLoginCta: "Anmelden",
  dashWrongPassword: "Benutzername oder Passwort stimmt nicht.",
  dashLogout: "Abmelden",
  dashToday: "Heute",
  dashTomorrow: "Morgen",
  dashWeek: "7 Tage",
  dashMonth: "Monat",
  dashAll: "Alle",
  srcWebsite: "Website",
  srcWhatsapp: "WhatsApp",
  srcPhone: "Telefon",
  stReservations: "Reservierungen",
  stGuests: "Personen",
  stOpen: "Offen",
  statusRequested: "Angefragt",
  statusConfirmed: "Bestätigt",
  statusSeated: "Am Tisch",
  statusCancelled: "Abgesagt",
  statusNoShow: "Nicht erschienen",
  actConfirm: "Bestätigen",
  actSeated: "Angekommen",
  actCancel: "Absagen",
  actNoShow: "Nicht erschienen",
  actDelete: "Löschen",
  actRestore: "Wiederherstellen",
  newReservation: "Neue Reservierung",
  source: "Kanal",
  save: "Speichern",
  cancel: "Abbrechen",
  dashEmpty: "Keine Reservierungen in diesem Zeitraum.",
  dashLoadError: "Reservierungen konnten nicht geladen werden.",
  showCancelled: "Abgesagte anzeigen",
  dashHint: "Website-Anfragen kommen als „Angefragt“ an — bitte bestätigen oder anrufen.",
  confirmDelete: "Wirklich löschen?",

  // menu editor (staff)
  tabReservations: "Reservierungen",
  tabMenu: "Karte bearbeiten",
  tabBusiness: "Öffnungszeiten & Kontakt",
  teamLogin: "Team-Login",
  bizEditorTitle: "Öffnungszeiten & Kontakt",
  bizEditorHint: "Öffnungszeiten, Adresse und Telefon — erscheinen sofort auf der Website, sobald du speicherst.",
  bizContact: "Kontakt & Adresse",
  bizName: "Name des Hauses",
  bizAddress: "Straße & Hausnummer",
  bizCity: "PLZ & Ort",
  bizMaps: "Google-Maps-Link",
  bizInstagram: "Instagram-Link",
  bizHoursHint: "Schließt das Haus nach Mitternacht, einfach z. B. 01:00 eintragen.",
  bizOpenDay: "Geöffnet",
  bizOpens: "Öffnet",
  bizCloses: "Schließt",
  bizLoadError: "Die Angaben konnten nicht geladen werden.",
  bizInvalid: "Bitte die markierten Felder prüfen.",
  bizDayparts: "Tageszeiten",
  bizDaypartsHint: "Die Zeiten auf der Startseite: Morgens, Nachmittags, Abends (Cocktails).",
  daypartFrom: "Von",
  daypartTo: "Bis",
  daypartTime: "{from} – {to} Uhr",
  menuEditorTitle: "Die Karte",
  menuEditorHint: "Änderungen erscheinen sofort auf der Website, sobald du speicherst.",
  addCategory: "Kategorie hinzufügen",
  addItem: "Gericht hinzufügen",
  editItem: "Bearbeiten",
  categoryName: "Kategorie",
  categoryDesc: "Untertitel",
  itemName: "Name",
  itemDesc: "Beschreibung",
  price: "Preis (€)",
  imageUrl: "Bild-URL",
  diet: "Ernährung",
  dietNone: "—",
  available: "Verfügbar",
  unavailable: "Ausverkauft",
  favourite: "Hausliebling",
  daypart: "Tageszeit",
  daypartMorning: "Morgens",
  daypartAfternoon: "Nachmittags",
  daypartEvening: "Abends",
  daypartAll: "Ganztags",
  moveUp: "Nach oben",
  moveDown: "Nach unten",
  deleteCategory: "Kategorie löschen",
  deleteItem: "Gericht löschen",
  optionGroups: "Optionen",
  addGroup: "Optionsgruppe",
  addOption: "Option",
  groupSingle: "Eine Wahl",
  groupMulti: "Mehrfach",
  groupRequired: "Pflicht",
  optionDefault: "Standard",
  extraPrice: "Aufpreis (€)",
  saveChanges: "Änderungen speichern",
  unsavedChanges: "Ungespeicherte Änderungen",
  savedOk: "Gespeichert",
  discard: "Verwerfen",
  confirmDeleteCategory: "Kategorie mit allen Gerichten löschen?",
  languageTab: "Sprache",
  menuLoadError: "Karte konnte nicht geladen werden.",
  newCategoryName: "Neue Kategorie",
  newItemName: "Neues Gericht",
  uploadPhoto: "Foto hochladen",
  uploadFailed: "Upload fehlgeschlagen — noch einmal?",
} as const;

export type StringKey = keyof typeof de;

const en: Record<StringKey, string> = {
  brand: "dähääm",
  brandSub: "Kurpfälzer Wohnzimmer",
  brandLine: "Café — Bar",
  navMenu: "Menu",
  navReserve: "Reserve",
  navStory: "The House",
  navHours: "Opening hours",
  skipToContent: "Skip to content",
  language: "Language",
  back: "Back",
  close: "Close",
  loading: "Loading …",
  retry: "Try again",
  somethingWrong: "Something went wrong.",

  openNow: "Open now",
  closedNow: "Closed right now",
  closesAt: "until {time}",
  opensAt: "opens {day} at {time}",
  today: "today",
  tomorrow: "tomorrow",
  closedLabel: "Closed",

  heroEyebrow: "Kurpfälzer Wohnzimmer · Café — Bar",
  heroTitle1: "Arrive.",
  heroTitle2: "Stay.",
  heroTitle3: "Feel at home.",
  heroLead:
    "A living room in the old town: brunch in the morning, coffee and cake in the afternoon, good drinks at night. No stiffness.",
  heroCtaReserve: "Book a table",
  heroCtaMenu: "See the menu",
  heroScroll: "Come in",

  marquee: "Brunch · Café · Bar · Kurpfälzer Wohnzimmer · In the heart of the old town · Time to stay",

  dayEyebrow: "A day at dähääm",
  dayTitle: "From the first cup to the last glass.",
  morningTitle: "Mornings",
  morningTime: "9 am – 2 pm",
  morningBody: "The day starts like in Milan: tramezzini, a croissant with butter and honey, a cappuccino. With your laptop by the window or the paper on the bench.",
  afternoonTitle: "Afternoons",
  afternoonTime: "2 – 6 pm",
  afternoonBody: "Cake from the pâtissier, a bowl or the soup of the day, a coffee alongside. A place to study and unwind – like the living room at your grandmother’s.",
  eveningTitle: "Evenings",
  eveningTime: "6 – 11 pm",
  eveningBody: "Cocktails, wine from the Palatinate, Dähäämuletta and small dishes from the oven to share. The lights go down, the bar opens up.",

  roomEyebrow: "Our story",
  roomTitle: "A living room, not a restaurant.",
  roomBody1: "Dähääm is Palatinate dialect for “at home”, and that is exactly how it should feel here: like a cosy living room in the heart of Heidelberg’s old town, where people like to meet in the morning, at noon or at night.",
  roomBody2: "The idea for our tramezzini came back with Alex from a trip to Milan, where he discovered the typically Italian sandwiches at breakfast and wanted to bring this piece of everyday Italian culture to Heidelberg. Today Dähääm pairs that inspiration with tramezzini, bowls, soups, coffee and a select list of cocktails: uncomplicated, personal, and for every time of day.",
  roomPoint1: "A living room, not a restaurant",
  roomPoint2: "Tramezzini like in Milan",
  roomPoint3: "Bowls, soups, coffee & cocktails",
  roomPoint4: "Uncomplicated, personal, any time of day",

  signatureEyebrow: "From the menu",
  signatureTitle: "Things worth trying.",
  viewFullMenu: "See the full menu",

  manifestoQuote: "Our aim is that you feel completely at home with us!",
  manifestoBody: "Unwind, enjoy, celebrate in a relaxed, homely atmosphere – because dähääm … is still the loveliest place to be!",

  findEyebrow: "Hours & directions",
  findTitle: "Right in the old town.",
  findLead: "Two minutes from the market square, between the bookshop and the wine merchant. Look for the light in the window.",
  addressLabel: "Address",
  phoneLabel: "Phone",
  emailLabel: "Email",
  hoursLabel: "Opening hours",
  openMaps: "Get directions",
  mon: "Monday",
  tue: "Tuesday",
  wed: "Wednesday",
  thu: "Thursday",
  fri: "Friday",
  sat: "Saturday",
  sun: "Sunday",

  reserveEyebrow: "Reservation",
  reserveTitle: "Keep a seat for you.",
  reserveLead: "Up to 25 people directly online. Larger parties, celebrations and the whole living room: give us a quick call.",
  date: "Date",
  time: "Time",
  guests: "Guests",
  name: "Name",
  phone: "Phone",
  email: "Email",
  occasion: "Occasion",
  occasionNone: "Just because",
  occasionBirthday: "Birthday",
  occasionDate: "Date night",
  occasionBusiness: "Business",
  occasionBrunch: "Brunch with friends",
  notes: "Requests",
  notesPlaceholder: "Window seat, high chair, allergies …",
  submitReservation: "Request reservation",
  sending: "Sending …",
  reserveSuccessTitle: "The table is yours.",
  reserveSuccessBody: "Confirmation code {code}. We only get in touch if something does not work out — otherwise: see you {when}.",
  reserveAnother: "Another reservation",
  requiredField: "Please fill this in",
  invalidPhone: "Please enter a valid phone number",
  invalidEmail: "Please enter a valid email",
  largeGroupHint: "For 26 or more, please call — we will happily push tables together.",
  noSlots: "We are closed on this day.",
  pastSlot: "That time has already passed.",
  reserveSummary: "{guests} guests · {when}",
  callUs: "Call us",
  reservePrivacy: "By sending you agree that we use your details to handle the reservation. Details:",
  seating: "Seating",
  seatingInside: "Inside",
  seatingOutside: "Outside",
  seatingAny: "No preference",
  openHint: "Open {open} – {close}",
  timeClosed: "We are closed at that time.",
  closedDayTitle: "We are closed on this day.",
  closedDayBody: "Please pick a different day.",
  openDaysLabel: "Open: {days}",

  menuEyebrow: "The menu",
  menuTitle: "Morning, noon and night.",
  menuLead: "Everything homemade, much of it regional — and all of it meant for staying a while.",
  allergens: "Allergens",
  vegan: "vegan",
  vegetarian: "vegetarian",
  seasonal: "seasonal",
  houseFavourite: "House favourite",
  soldOut: "Sold out",
  from: "from",
  menuNote: "To enjoy here. Menu and prices change with the season.",
  choiceOf: "Choice of",

  footerTagline: "A modern Palatinate living room.",
  footerRights: "All rights reserved",
  imprint: "Imprint",
  privacy: "Privacy",
  followUs: "Follow",
  newsletterTitle: "News from the living room",
  newsletterBody: "Seasonal menu, wine evenings, Sunday brunch — once a month, never more.",
  newsletterPlaceholder: "your@mail.com",
  newsletterCta: "Sign up",
  newsletterDone: "Thank you! We will be in touch.",

  dashTitle: "Reservation book",
  dashSub: "Website · WhatsApp · Phone",
  dashLoginTitle: "Team only",
  dashUsername: "Username",
  dashPassword: "Password",
  dashLoginCta: "Sign in",
  dashWrongPassword: "Username or password is not right.",
  dashLogout: "Sign out",
  dashToday: "Today",
  dashTomorrow: "Tomorrow",
  dashWeek: "7 days",
  dashMonth: "Month",
  dashAll: "All",
  srcWebsite: "Website",
  srcWhatsapp: "WhatsApp",
  srcPhone: "Phone",
  stReservations: "Reservations",
  stGuests: "Guests",
  stOpen: "Open",
  statusRequested: "Requested",
  statusConfirmed: "Confirmed",
  statusSeated: "Seated",
  statusCancelled: "Cancelled",
  statusNoShow: "No-show",
  actConfirm: "Confirm",
  actSeated: "Arrived",
  actCancel: "Cancel",
  actNoShow: "No-show",
  actDelete: "Delete",
  actRestore: "Restore",
  newReservation: "New reservation",
  source: "Channel",
  save: "Save",
  cancel: "Cancel",
  dashEmpty: "No reservations in this period.",
  dashLoadError: "Could not load reservations.",
  showCancelled: "Show cancelled",
  dashHint: "Website requests arrive as “Requested” — confirm them or call back.",
  confirmDelete: "Really delete?",

  tabReservations: "Reservations",
  tabMenu: "Edit menu",
  tabBusiness: "Hours & contact",
  teamLogin: "Team login",
  bizEditorTitle: "Hours & contact",
  bizEditorHint: "Opening hours, address and phone — they appear on the website the moment you save.",
  bizContact: "Contact & address",
  bizName: "Name of the house",
  bizAddress: "Street & number",
  bizCity: "Postcode & town",
  bizMaps: "Google Maps link",
  bizInstagram: "Instagram link",
  bizHoursHint: "Closing after midnight? Just enter 01:00, for example.",
  bizOpenDay: "Open",
  bizOpens: "Opens",
  bizCloses: "Closes",
  bizLoadError: "Could not load the details.",
  bizInvalid: "Please check the marked fields.",
  bizDayparts: "Times of day",
  bizDaypartsHint: "The times shown on the home page: mornings, afternoons, evenings (cocktails).",
  daypartFrom: "From",
  daypartTo: "To",
  daypartTime: "{from} – {to}",
  menuEditorTitle: "The menu",
  menuEditorHint: "Changes go live on the website as soon as you save.",
  addCategory: "Add category",
  addItem: "Add dish",
  editItem: "Edit",
  categoryName: "Category",
  categoryDesc: "Subtitle",
  itemName: "Name",
  itemDesc: "Description",
  price: "Price (€)",
  imageUrl: "Image URL",
  diet: "Diet",
  dietNone: "—",
  available: "Available",
  unavailable: "Sold out",
  favourite: "House favourite",
  daypart: "Time of day",
  daypartMorning: "Morning",
  daypartAfternoon: "Afternoon",
  daypartEvening: "Evening",
  daypartAll: "All day",
  moveUp: "Move up",
  moveDown: "Move down",
  deleteCategory: "Delete category",
  deleteItem: "Delete dish",
  optionGroups: "Options",
  addGroup: "Option group",
  addOption: "Option",
  groupSingle: "Pick one",
  groupMulti: "Pick several",
  groupRequired: "Required",
  optionDefault: "Default",
  extraPrice: "Extra (€)",
  saveChanges: "Save changes",
  unsavedChanges: "Unsaved changes",
  savedOk: "Saved",
  discard: "Discard",
  confirmDeleteCategory: "Delete the category and all its dishes?",
  languageTab: "Language",
  menuLoadError: "Could not load the menu.",
  newCategoryName: "New category",
  newItemName: "New dish",
  uploadPhoto: "Upload photo",
  uploadFailed: "Upload failed — try again?",
};

const fr: Record<StringKey, string> = {
  brand: "dähääm",
  brandSub: "Kurpfälzer Wohnzimmer",
  brandLine: "Café — Bar",
  navMenu: "La carte",
  navReserve: "Réserver",
  navStory: "La maison",
  navHours: "Horaires",
  skipToContent: "Aller au contenu",
  language: "Langue",
  back: "Retour",
  close: "Fermer",
  loading: "Chargement …",
  retry: "Réessayer",
  somethingWrong: "Quelque chose s'est mal passé.",

  openNow: "Ouvert",
  closedNow: "Fermé pour le moment",
  closesAt: "jusqu'à {time}",
  opensAt: "ouvre {day} à {time}",
  today: "aujourd'hui",
  tomorrow: "demain",
  closedLabel: "Fermé",

  heroEyebrow: "Kurpfälzer Wohnzimmer · Café — Bar",
  heroTitle1: "Arriver.",
  heroTitle2: "Rester.",
  heroTitle3: "Se sentir chez soi.",
  heroLead:
    "Un salon dans la vieille ville : brunch le matin, café et gâteaux l'après-midi, bons cocktails le soir. Sans raideur.",
  heroCtaReserve: "Réserver une table",
  heroCtaMenu: "Voir la carte",
  heroScroll: "Entrez",

  marquee: "Brunch · Café · Bar · Kurpfälzer Wohnzimmer · Au cœur de la vieille ville · Le temps de rester",

  dayEyebrow: "Une journée chez dähääm",
  dayTitle: "De la première tasse au dernier verre.",
  morningTitle: "Le matin",
  morningTime: "9 h – 14 h",
  morningBody: "La journée commence comme à Milan : tramezzini, croissant au beurre et au miel, cappuccino. Avec l’ordinateur près de la fenêtre ou le journal sur la banquette.",
  afternoonTitle: "L'après-midi",
  afternoonTime: "14 h – 18 h",
  afternoonBody: "Gâteau du pâtissier, un bowl ou la soupe du jour, un café avec. Un endroit pour étudier et se détendre – comme le salon de grand-mère.",
  eveningTitle: "Le soir",
  eveningTime: "18 h – 23 h",
  eveningBody: "Cocktails, vins du Palatinat, Dähäämuletta et petits plats au four à partager. Les lumières baissent, le bar s’ouvre.",

  roomEyebrow: "Notre histoire",
  roomTitle: "Un salon, pas un restaurant.",
  roomBody1: "Dähääm, en dialecte palatin, veut dire « à la maison », et c’est exactement ce que l’on doit ressentir ici : un salon chaleureux au cœur de la vieille ville de Heidelberg, où l’on aime se retrouver le matin, le midi ou le soir.",
  roomBody2: "L’idée de nos tramezzini, Alex l’a rapportée d’un voyage à Milan : il y a découvert ces sandwichs typiquement italiens au petit-déjeuner et a voulu amener ce morceau de quotidien italien à Heidelberg. Aujourd’hui, Dähääm marie cette inspiration à des tramezzini, des bowls, des soupes, du café et une sélection de cocktails : simple, personnel, à toute heure.",
  roomPoint1: "Un salon, pas un restaurant",
  roomPoint2: "Des tramezzini comme à Milan",
  roomPoint3: "Bowls, soupes, café & cocktails",
  roomPoint4: "Simple, personnel, à toute heure",

  signatureEyebrow: "De la carte",
  signatureTitle: "À goûter absolument.",
  viewFullMenu: "Voir toute la carte",

  manifestoQuote: "Notre ambition : que vous vous sentiez chez nous comme à la maison !",
  manifestoBody: "Décompresser, savourer, faire la fête dans une ambiance détendue et chaleureuse – parce que dähääm … c'est toujours là qu'on est le mieux !",

  findEyebrow: "Horaires & accès",
  findTitle: "Au cœur de la vieille ville.",
  findLead: "À deux minutes de la place du marché, entre la librairie et le caviste. Cherchez la lumière dans la fenêtre.",
  addressLabel: "Adresse",
  phoneLabel: "Téléphone",
  emailLabel: "E-mail",
  hoursLabel: "Horaires",
  openMaps: "Itinéraire",
  mon: "Lundi",
  tue: "Mardi",
  wed: "Mercredi",
  thu: "Jeudi",
  fri: "Vendredi",
  sat: "Samedi",
  sun: "Dimanche",

  reserveEyebrow: "Réservation",
  reserveTitle: "On vous garde une place.",
  reserveLead: "Jusqu'à 25 personnes directement en ligne. Grandes tablées, fêtes et tout le salon : appelez-nous.",
  date: "Date",
  time: "Heure",
  guests: "Personnes",
  name: "Nom",
  phone: "Téléphone",
  email: "E-mail",
  occasion: "Occasion",
  occasionNone: "Comme ça",
  occasionBirthday: "Anniversaire",
  occasionDate: "Rendez-vous",
  occasionBusiness: "Professionnel",
  occasionBrunch: "Brunch entre amis",
  notes: "Souhaits",
  notesPlaceholder: "Près de la fenêtre, chaise haute, allergies …",
  submitReservation: "Demander une réservation",
  sending: "Envoi …",
  reserveSuccessTitle: "La table est à vous.",
  reserveSuccessBody: "Code de confirmation {code}. Nous ne vous contactons que si quelque chose ne va pas — sinon : à {when}.",
  reserveAnother: "Nouvelle réservation",
  requiredField: "Champ obligatoire",
  invalidPhone: "Merci d'indiquer un numéro valide",
  invalidEmail: "Merci d'indiquer un e-mail valide",
  largeGroupHint: "À partir de 26 personnes, appelez-nous — on rapproche les tables avec plaisir.",
  noSlots: "Nous sommes fermés ce jour-là.",
  pastSlot: "Cet horaire est déjà passé.",
  reserveSummary: "{guests} personnes · {when}",
  callUs: "Appeler",
  reservePrivacy: "En envoyant, vous acceptez que nous utilisions vos données pour traiter la réservation. Détails :",
  seating: "Place",
  seatingInside: "À l'intérieur",
  seatingOutside: "En terrasse",
  seatingAny: "Peu importe",
  openHint: "Ouvert {open} – {close}",
  timeClosed: "Nous sommes fermés à cette heure-là.",
  closedDayTitle: "Nous sommes fermés ce jour-là.",
  closedDayBody: "Merci de choisir un autre jour.",
  openDaysLabel: "Ouvert : {days}",

  menuEyebrow: "La carte",
  menuTitle: "Matin, midi et soir.",
  menuLead: "Tout est fait maison, beaucoup vient de la région — et tout est pensé pour rester un moment.",
  allergens: "Allergènes",
  vegan: "végan",
  vegetarian: "végétarien",
  seasonal: "de saison",
  houseFavourite: "Favori de la maison",
  soldOut: "Épuisé",
  from: "dès",
  menuNote: "À déguster sur place. Carte et prix changent avec les saisons.",
  choiceOf: "Au choix",

  footerTagline: "Un salon palatin moderne.",
  footerRights: "Tous droits réservés",
  imprint: "Mentions légales",
  privacy: "Confidentialité",
  followUs: "Suivre",
  newsletterTitle: "Des nouvelles du salon",
  newsletterBody: "Carte de saison, soirées vin, brunch du dimanche — une fois par mois, jamais plus.",
  newsletterPlaceholder: "votre@mail.fr",
  newsletterCta: "S'inscrire",
  newsletterDone: "Merci ! À bientôt.",

  dashTitle: "Livre de réservations",
  dashSub: "Site · WhatsApp · Téléphone",
  dashLoginTitle: "Équipe uniquement",
  dashUsername: "Nom d'utilisateur",
  dashPassword: "Mot de passe",
  dashLoginCta: "Se connecter",
  dashWrongPassword: "Nom d'utilisateur ou mot de passe incorrect.",
  dashLogout: "Se déconnecter",
  dashToday: "Aujourd'hui",
  dashTomorrow: "Demain",
  dashWeek: "7 jours",
  dashMonth: "Mois",
  dashAll: "Tous",
  srcWebsite: "Site web",
  srcWhatsapp: "WhatsApp",
  srcPhone: "Téléphone",
  stReservations: "Réservations",
  stGuests: "Personnes",
  stOpen: "En attente",
  statusRequested: "Demandée",
  statusConfirmed: "Confirmée",
  statusSeated: "À table",
  statusCancelled: "Annulée",
  statusNoShow: "Non venue",
  actConfirm: "Confirmer",
  actSeated: "Arrivés",
  actCancel: "Annuler",
  actNoShow: "Non venue",
  actDelete: "Supprimer",
  actRestore: "Rétablir",
  newReservation: "Nouvelle réservation",
  source: "Canal",
  save: "Enregistrer",
  cancel: "Annuler",
  dashEmpty: "Aucune réservation sur cette période.",
  dashLoadError: "Impossible de charger les réservations.",
  showCancelled: "Afficher les annulées",
  dashHint: "Les demandes du site arrivent en « Demandée » — confirmez-les ou rappelez.",
  confirmDelete: "Vraiment supprimer ?",

  tabReservations: "Réservations",
  tabMenu: "Modifier la carte",
  tabBusiness: "Horaires & contact",
  teamLogin: "Connexion équipe",
  bizEditorTitle: "Horaires & contact",
  bizEditorHint: "Horaires, adresse et téléphone — visibles sur le site dès que vous enregistrez.",
  bizContact: "Contact & adresse",
  bizName: "Nom de la maison",
  bizAddress: "Rue & numéro",
  bizCity: "Code postal & ville",
  bizMaps: "Lien Google Maps",
  bizInstagram: "Lien Instagram",
  bizHoursHint: "Fermeture après minuit ? Indiquez simplement 01:00, par exemple.",
  bizOpenDay: "Ouvert",
  bizOpens: "Ouvre",
  bizCloses: "Ferme",
  bizLoadError: "Impossible de charger les informations.",
  bizInvalid: "Veuillez vérifier les champs signalés.",
  bizDayparts: "Moments de la journée",
  bizDaypartsHint: "Les horaires affichés sur la page d'accueil : matin, après-midi, soir (cocktails).",
  daypartFrom: "De",
  daypartTo: "À",
  daypartTime: "{from} – {to}",
  menuEditorTitle: "La carte",
  menuEditorHint: "Les modifications apparaissent sur le site dès que vous enregistrez.",
  addCategory: "Ajouter une catégorie",
  addItem: "Ajouter un plat",
  editItem: "Modifier",
  categoryName: "Catégorie",
  categoryDesc: "Sous-titre",
  itemName: "Nom",
  itemDesc: "Description",
  price: "Prix (€)",
  imageUrl: "URL de l'image",
  diet: "Régime",
  dietNone: "—",
  available: "Disponible",
  unavailable: "Épuisé",
  favourite: "Favori de la maison",
  daypart: "Moment",
  daypartMorning: "Matin",
  daypartAfternoon: "Après-midi",
  daypartEvening: "Soir",
  daypartAll: "Toute la journée",
  moveUp: "Monter",
  moveDown: "Descendre",
  deleteCategory: "Supprimer la catégorie",
  deleteItem: "Supprimer le plat",
  optionGroups: "Options",
  addGroup: "Groupe d'options",
  addOption: "Option",
  groupSingle: "Un seul choix",
  groupMulti: "Plusieurs",
  groupRequired: "Obligatoire",
  optionDefault: "Par défaut",
  extraPrice: "Supplément (€)",
  saveChanges: "Enregistrer",
  unsavedChanges: "Modifications non enregistrées",
  savedOk: "Enregistré",
  discard: "Annuler",
  confirmDeleteCategory: "Supprimer la catégorie et tous ses plats ?",
  languageTab: "Langue",
  menuLoadError: "Impossible de charger la carte.",
  newCategoryName: "Nouvelle catégorie",
  newItemName: "Nouveau plat",
  uploadPhoto: "Téléverser une photo",
  uploadFailed: "Échec de l'envoi — réessayer ?",
};

const es: Record<StringKey, string> = {
  brand: "dähääm",
  brandSub: "Kurpfälzer Wohnzimmer",
  brandLine: "Café — Bar",
  navMenu: "La carta",
  navReserve: "Reservar",
  navStory: "La casa",
  navHours: "Horario",
  skipToContent: "Ir al contenido",
  language: "Idioma",
  back: "Volver",
  close: "Cerrar",
  loading: "Cargando …",
  retry: "Reintentar",
  somethingWrong: "Algo ha salido mal.",
  openNow: "Abierto ahora",
  closedNow: "Cerrado ahora",
  closesAt: "hasta las {time}",
  opensAt: "abre {day} a las {time}",
  today: "hoy",
  tomorrow: "mañana",
  closedLabel: "Cerrado",
  heroEyebrow: "Kurpfälzer Wohnzimmer · Café — Bar",
  heroTitle1: "Llegar.",
  heroTitle2: "Quedarse.",
  heroTitle3: "Sentirse en casa.",
  heroLead: "Un salón en el casco antiguo: brunch por la mañana, café y tarta por la tarde, buenas copas por la noche. Sin rigidez.",
  heroCtaReserve: "Reservar mesa",
  heroCtaMenu: "Ver la carta",
  heroScroll: "Entra",
  marquee: "Brunch · Café · Bar · Kurpfälzer Wohnzimmer · En pleno casco antiguo · Tiempo para quedarse",
  dayEyebrow: "Un día en dähääm",
  dayTitle: "De la primera taza a la última copa.",
  morningTitle: "Por la mañana",
  morningTime: "9 – 14 h",
  morningBody: "El día empieza como en Milán: tramezzini, un cruasán con mantequilla y miel, un capuchino. Con el portátil junto a la ventana o el periódico en el banco.",
  afternoonTitle: "Por la tarde",
  afternoonTime: "14 – 18 h",
  afternoonBody: "Tarta del pastelero, un bowl o la sopa del día, con un café. Un lugar para estudiar y relajarse – como el salón de la abuela.",
  eveningTitle: "Por la noche",
  eveningTime: "18 – 23 h",
  eveningBody: "Cócteles, vino del Palatinado, Dähäämuletta y platitos del horno para compartir. Bajan las luces, abre el bar.",
  roomEyebrow: "Nuestra historia",
  roomTitle: "Un salón, no un restaurante.",
  roomBody1: "Dähääm significa «en casa» en el dialecto del Palatinado, y así debe sentirse aquí: como un salón acogedor en pleno casco antiguo de Heidelberg, donde apetece quedar por la mañana, al mediodía o por la noche.",
  roomBody2: "La idea de nuestros tramezzini la trajo Alex de un viaje a Milán: allí descubrió estos sándwiches típicamente italianos en el desayuno y quiso traer ese pedazo de cultura cotidiana italiana a Heidelberg. Hoy Dähääm une esa inspiración con tramezzini, bowls, sopas, café y una selección de cócteles: sencillo, personal y para cualquier hora del día.",
  roomPoint1: "Un salón, no un restaurante",
  roomPoint2: "Tramezzini como en Milán",
  roomPoint3: "Bowls, sopas, café y cócteles",
  roomPoint4: "Sencillo, personal, a cualquier hora",
  signatureEyebrow: "De la carta",
  signatureTitle: "Lo que hay que probar.",
  viewFullMenu: "Ver toda la carta",
  manifestoQuote: "Nuestra aspiración es que os sintáis en nuestra casa como en la vuestra.",
  manifestoBody: "Desconectar, disfrutar, celebrar en un ambiente relajado y acogedor – porque dähääm … sigue siendo el lugar más bonito.",
  findEyebrow: "Horario y cómo llegar",
  findTitle: "En pleno casco antiguo.",
  findLead: "A dos minutos de la plaza del mercado, entre la librería y la vinoteca. Busca la luz en la ventana.",
  addressLabel: "Dirección",
  phoneLabel: "Teléfono",
  emailLabel: "Correo",
  hoursLabel: "Horario",
  openMaps: "Cómo llegar",
  mon: "Lunes",
  tue: "Martes",
  wed: "Miércoles",
  thu: "Jueves",
  fri: "Viernes",
  sat: "Sábado",
  sun: "Domingo",
  reserveEyebrow: "Reserva",
  reserveTitle: "Te guardamos sitio.",
  reserveLead: "Hasta 25 personas directamente online. Grupos grandes, celebraciones y todo el salón: llámanos.",
  date: "Fecha",
  time: "Hora",
  guests: "Personas",
  name: "Nombre",
  phone: "Teléfono",
  email: "Correo",
  occasion: "Ocasión",
  occasionNone: "Porque sí",
  occasionBirthday: "Cumpleaños",
  occasionDate: "Cita",
  occasionBusiness: "Trabajo",
  occasionBrunch: "Brunch con amigos",
  notes: "Deseos",
  notesPlaceholder: "Junto a la ventana, trona, alergias …",
  submitReservation: "Solicitar reserva",
  sending: "Enviando …",
  reserveSuccessTitle: "La mesa es vuestra.",
  reserveSuccessBody: "Código de confirmación {code}. Solo os avisamos si algo no cuadra — si no: hasta el {when}.",
  reserveAnother: "Otra reserva",
  requiredField: "Campo obligatorio",
  invalidPhone: "Indica un teléfono válido",
  invalidEmail: "Indica un correo válido",
  largeGroupHint: "A partir de 26 personas, llámanos — juntamos mesas con gusto.",
  noSlots: "Ese día cerramos.",
  pastSlot: "Esa hora ya ha pasado.",
  reserveSummary: "{guests} personas · {when}",
  callUs: "Llamar",
  reservePrivacy: "Al enviar aceptas que usemos tus datos para gestionar la reserva. Detalles:",
  seating: "Mesa",
  seatingInside: "Dentro",
  seatingOutside: "Fuera",
  seatingAny: "Indiferente",
  openHint: "Abierto {open} – {close}",
  timeClosed: "A esa hora estamos cerrados.",
  closedDayTitle: "Ese día cerramos.",
  closedDayBody: "Elige otro día, por favor.",
  openDaysLabel: "Abierto: {days}",
  menuEyebrow: "La carta",
  menuTitle: "Mañana, mediodía y noche.",
  menuLead: "Todo casero, mucho de la región — y todo pensado para quedarse un rato.",
  allergens: "Alérgenos",
  vegan: "vegano",
  vegetarian: "vegetariano",
  seasonal: "de temporada",
  houseFavourite: "Favorito de la casa",
  soldOut: "Agotado",
  from: "desde",
  menuNote: "Para disfrutar aquí. Carta y precios cambian con la temporada.",
  choiceOf: "A elegir",
  footerTagline: "Un salón palatino moderno.",
  footerRights: "Todos los derechos reservados",
  imprint: "Aviso legal",
  privacy: "Privacidad",
  followUs: "Síguenos",
  newsletterTitle: "Novedades del salón",
  newsletterBody: "Carta de temporada, noches de vino, brunch dominical — una vez al mes, nunca más.",
  newsletterPlaceholder: "tu@correo.es",
  newsletterCta: "Apuntarme",
  newsletterDone: "¡Gracias! Te escribimos.",
  dashTitle: "Libro de reservas",
  dashSub: "Web · WhatsApp · Teléfono",
  dashLoginTitle: "Solo para el equipo",
  dashUsername: "Usuario",
  dashPassword: "Contraseña",
  dashLoginCta: "Entrar",
  dashWrongPassword: "Usuario o contraseña incorrectos.",
  dashLogout: "Salir",
  dashToday: "Hoy",
  dashTomorrow: "Mañana",
  dashWeek: "7 días",
  dashMonth: "Mes",
  dashAll: "Todas",
  srcWebsite: "Web",
  srcWhatsapp: "WhatsApp",
  srcPhone: "Teléfono",
  stReservations: "Reservas",
  stGuests: "Personas",
  stOpen: "Pendientes",
  statusRequested: "Solicitada",
  statusConfirmed: "Confirmada",
  statusSeated: "En mesa",
  statusCancelled: "Cancelada",
  statusNoShow: "No vino",
  actConfirm: "Confirmar",
  actSeated: "Han llegado",
  actCancel: "Cancelar",
  actNoShow: "No vino",
  actDelete: "Eliminar",
  actRestore: "Restaurar",
  newReservation: "Nueva reserva",
  source: "Canal",
  save: "Guardar",
  cancel: "Cancelar",
  dashEmpty: "No hay reservas en este periodo.",
  dashLoadError: "No se pudieron cargar las reservas.",
  showCancelled: "Mostrar canceladas",
  dashHint: "Las solicitudes de la web llegan como «Solicitada» — confírmalas o devuelve la llamada.",
  confirmDelete: "¿Eliminar de verdad?",

  tabReservations: "Reservas",
  tabMenu: "Editar carta",
  tabBusiness: "Horario y contacto",
  teamLogin: "Acceso equipo",
  bizEditorTitle: "Horario y contacto",
  bizEditorHint: "Horario, dirección y teléfono — aparecen en la web en cuanto guardas.",
  bizContact: "Contacto y dirección",
  bizName: "Nombre de la casa",
  bizAddress: "Calle y número",
  bizCity: "Código postal y ciudad",
  bizMaps: "Enlace de Google Maps",
  bizInstagram: "Enlace de Instagram",
  bizHoursHint: "¿Cierra después de medianoche? Pon simplemente 01:00, por ejemplo.",
  bizOpenDay: "Abierto",
  bizOpens: "Abre",
  bizCloses: "Cierra",
  bizLoadError: "No se pudieron cargar los datos.",
  bizInvalid: "Revisa los campos marcados.",
  bizDayparts: "Momentos del día",
  bizDaypartsHint: "Los horarios que se muestran en la portada: mañana, tarde, noche (cócteles).",
  daypartFrom: "De",
  daypartTo: "A",
  daypartTime: "{from} – {to}",
  menuEditorTitle: "La carta",
  menuEditorHint: "Los cambios aparecen en la web en cuanto guardas.",
  addCategory: "Añadir categoría",
  addItem: "Añadir plato",
  editItem: "Editar",
  categoryName: "Categoría",
  categoryDesc: "Subtítulo",
  itemName: "Nombre",
  itemDesc: "Descripción",
  price: "Precio (€)",
  imageUrl: "URL de la imagen",
  diet: "Dieta",
  dietNone: "—",
  available: "Disponible",
  unavailable: "Agotado",
  favourite: "Favorito de la casa",
  daypart: "Momento del día",
  daypartMorning: "Mañana",
  daypartAfternoon: "Tarde",
  daypartEvening: "Noche",
  daypartAll: "Todo el día",
  moveUp: "Subir",
  moveDown: "Bajar",
  deleteCategory: "Eliminar categoría",
  deleteItem: "Eliminar plato",
  optionGroups: "Opciones",
  addGroup: "Grupo de opciones",
  addOption: "Opción",
  groupSingle: "Una opción",
  groupMulti: "Varias",
  groupRequired: "Obligatorio",
  optionDefault: "Por defecto",
  extraPrice: "Suplemento (€)",
  saveChanges: "Guardar cambios",
  unsavedChanges: "Cambios sin guardar",
  savedOk: "Guardado",
  discard: "Descartar",
  confirmDeleteCategory: "¿Eliminar la categoría con todos sus platos?",
  languageTab: "Idioma",
  menuLoadError: "No se pudo cargar la carta.",
  newCategoryName: "Nueva categoría",
  newItemName: "Nuevo plato",
  uploadPhoto: "Subir foto",
  uploadFailed: "Error al subir — ¿otra vez?",
};

export const STRINGS: Record<Language, Record<StringKey, string>> = { de, en, fr, es };
